import React, { useMemo, useState } from 'react';
import { FiCalendar, FiPlus, FiTrash2 } from 'react-icons/fi';
import { PRODUCT_OPTIONS } from '../../utils/zovcurInvoiceStore';

const STATUS_OPTIONS = ['Pending', 'Visited', 'Completed', 'Cancelled'];
const HOUR_OPTIONS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MINUTE_OPTIONS = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
const PERIOD_OPTIONS = ['AM', 'PM'];

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const DailyPlan = () => {
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    materialName: '',
    doctorName: '',
    clinicName: '',
    appointmentFrom: { hour: '', minute: '', period: 'AM' },
    appointmentTo: { hour: '', minute: '', period: 'AM' },
    orderQuantity: '',
    status: 'Pending',
    photoFile: null,
    photoPreview: ''
  });

  const totalPlans = useMemo(() => plans.length, [plans]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleTimeChange = (field, part, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [part]: value
      }
    }));
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const preview = await toBase64(file);
    setFormData((prev) => ({
      ...prev,
      photoFile: file,
      photoPreview: preview
    }));
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      materialName: '',
      doctorName: '',
      clinicName: '',
      appointmentFrom: { hour: '', minute: '', period: 'AM' },
      appointmentTo: { hour: '', minute: '', period: 'AM' },
      orderQuantity: '',
      status: 'Pending',
      photoFile: null,
      photoPreview: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fromTime = `${formData.appointmentFrom.hour}:${formData.appointmentFrom.minute} ${formData.appointmentFrom.period}`;
    const toTime = `${formData.appointmentTo.hour}:${formData.appointmentTo.minute} ${formData.appointmentTo.period}`;
    const entry = {
      id: `${Date.now()}`,
      date: formData.date,
      materialName: formData.materialName,
      doctorName: formData.doctorName,
      clinicName: formData.clinicName,
      appointmentFrom: fromTime,
      appointmentTo: toTime,
      orderQuantity: Number(formData.orderQuantity) || 0,
      status: formData.status,
      photoPreview: formData.photoPreview
    };
    setPlans((prev) => [entry, ...prev]);
    resetForm();
  };

  const handleDelete = (id) => {
    setPlans((prev) => prev.filter((plan) => plan.id !== id));
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold text-gray-800">
            <FiCalendar className="text-blue-600" />
            Daily Plan
          </h1>
          <p className="mt-1 text-gray-600">Add your daily doctor visit and order plan.</p>
        </div>
        <div className="rounded-lg bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
          Total Plans: {totalPlans}
        </div>
      </div>

      <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
        <h2 className="mb-4 text-xl font-semibold text-gray-800">Create Daily Plan</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Material Name</label>
              <select
                value={formData.materialName}
                onChange={(e) => handleChange('materialName', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              >
                <option value="">Select material</option>
                {PRODUCT_OPTIONS.map((material) => (
                  <option key={material} value={material}>
                    {material}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Doctor Name</label>
              <input
                type="text"
                value={formData.doctorName}
                onChange={(e) => handleChange('doctorName', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Clinic Name</label>
              <input
                type="text"
                value={formData.clinicName}
                onChange={(e) => handleChange('clinicName', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Appointment From</label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={formData.appointmentFrom.hour}
                  onChange={(e) => handleTimeChange('appointmentFrom', 'hour', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2"
                  required
                >
                  <option value="">HH</option>
                  {HOUR_OPTIONS.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
                <select
                  value={formData.appointmentFrom.minute}
                  onChange={(e) => handleTimeChange('appointmentFrom', 'minute', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2"
                  required
                >
                  <option value="">MM</option>
                  {MINUTE_OPTIONS.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
                <select
                  value={formData.appointmentFrom.period}
                  onChange={(e) => handleTimeChange('appointmentFrom', 'period', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2"
                >
                  {PERIOD_OPTIONS.map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Appointment To</label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={formData.appointmentTo.hour}
                  onChange={(e) => handleTimeChange('appointmentTo', 'hour', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2"
                  required
                >
                  <option value="">HH</option>
                  {HOUR_OPTIONS.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
                <select
                  value={formData.appointmentTo.minute}
                  onChange={(e) => handleTimeChange('appointmentTo', 'minute', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2"
                  required
                >
                  <option value="">MM</option>
                  {MINUTE_OPTIONS.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
                <select
                  value={formData.appointmentTo.period}
                  onChange={(e) => handleTimeChange('appointmentTo', 'period', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2"
                >
                  {PERIOD_OPTIONS.map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Order Quantity</label>
              <input
                type="number"
                min="1"
                value={formData.orderQuantity}
                onChange={(e) => handleChange('orderQuantity', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Plan Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            {formData.photoPreview ? (
              <img
                src={formData.photoPreview}
                alt="Plan preview"
                className="mt-2 h-20 w-20 rounded-md border object-cover"
              />
            ) : null}
          </div>

          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
          >
            <FiPlus />
            Add Plan
          </button>
        </form>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-md">
        <h2 className="mb-4 text-xl font-semibold text-gray-800">Daily Plan List</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Date</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Material</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Doctor</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Clinic</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Time</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Qty</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Photo</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-3 py-8 text-center text-gray-500">
                    No daily plans added yet
                  </td>
                </tr>
              ) : (
                plans.map((plan) => (
                  <tr key={plan.id} className="border-b">
                    <td className="px-3 py-2 text-sm">{plan.date}</td>
                    <td className="px-3 py-2 text-sm">{plan.materialName}</td>
                    <td className="px-3 py-2 text-sm">{plan.doctorName}</td>
                    <td className="px-3 py-2 text-sm">{plan.clinicName}</td>
                    <td className="px-3 py-2 text-sm">
                      {plan.appointmentFrom} - {plan.appointmentTo}
                    </td>
                    <td className="px-3 py-2 text-sm">{plan.orderQuantity}</td>
                    <td className="px-3 py-2 text-sm">{plan.status}</td>
                    <td className="px-3 py-2 text-sm">
                      {plan.photoPreview ? (
                        <img
                          src={plan.photoPreview}
                          alt="Plan"
                          className="h-10 w-10 rounded border object-cover"
                        />
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <button
                        type="button"
                        onClick={() => handleDelete(plan.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyPlan;
